(() => {
  const WISHLIST_KEY = 'premiumWishlistProducts';
  const LEGACY_KEYS = ['gksen_wishlist'];

  const safeJson = (value, fallback) => {
    try {
      const parsed = JSON.parse(value);
      return parsed || fallback;
    } catch (error) {
      return fallback;
    }
  };

  const escapeHtml = (value) => String(value || '').replace(/[&<>'"]/g, (char) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', "'": '&#39;', '"': '&quot;' }[char]));
  const escapeAttr = escapeHtml;

  const normalizeStoredItem = (item) => {
    if (!item) return null;
    if (typeof item === 'string') {
      return { id: item, handle: item, title: 'Product', url: `/products/${item}` };
    }

    const handle = item.handle || item.productHandle || item.id || '';
    const id = item.id || handle;
    if (!handle && !id) return null;

    return {
      id: String(id || handle),
      handle: String(handle || id),
      title: item.title || item.productTitle || 'Product',
      url: item.url || item.productUrl || (handle ? `/products/${handle}` : '#'),
      image: item.image || item.productImage || '',
      price: item.price || item.productPrice || '',
      variantId: item.variantId || item.variant_id || item.productVariantId || ''
    };
  };

  const uniqueItems = (items) => {
    const seen = new Set();
    return (Array.isArray(items) ? items : [])
      .map(normalizeStoredItem)
      .filter(Boolean)
      .filter((item) => {
        const key = item.handle || item.id;
        if (!key || seen.has(key)) return false;
        seen.add(key);
        return true;
      });
  };

  const getWishlist = () => {
    let items = uniqueItems(safeJson(localStorage.getItem(WISHLIST_KEY), []));

    if (!items.length) {
      for (const key of LEGACY_KEYS) {
        const legacy = uniqueItems(safeJson(localStorage.getItem(key), []));
        if (legacy.length) {
          items = legacy;
          localStorage.setItem(WISHLIST_KEY, JSON.stringify(items));
          break;
        }
      }
    }

    return items;
  };

  const setWishlist = (items, options = {}) => {
    const normalized = uniqueItems(items);
    localStorage.setItem(WISHLIST_KEY, JSON.stringify(normalized));
    document.dispatchEvent(new CustomEvent('premium:wishlist:update', { detail: { items: normalized, action: options.action || '' } }));
    return normalized;
  };

  const normalizeProduct = (button) => ({
    id: button.dataset.productId || button.dataset.productHandle || '',
    handle: button.dataset.productHandle || button.dataset.productId || '',
    title: button.dataset.productTitle || 'Product',
    url: button.dataset.productUrl || (button.dataset.productHandle ? `/products/${button.dataset.productHandle}` : '#'),
    image: button.dataset.productImage || '',
    price: button.dataset.productPrice || '',
    variantId: button.dataset.productVariantId || button.dataset.variantId || ''
  });

  const isSameProduct = (item, product) => {
    if (item.handle && product.handle) return String(item.handle) === String(product.handle);
    return String(item.id) === String(product.id);
  };


  const updateCounters = () => {
    const count = getWishlist().length;
    document.querySelectorAll('[data-premium-wishlist-counter]').forEach((counter) => {
      counter.textContent = count > 99 ? '99+' : String(count);
      counter.hidden = count === 0;
      counter.setAttribute('aria-hidden', count === 0 ? 'true' : 'false');
    });
  };

  const updateButtons = (root = document) => {
    const wishlist = getWishlist();
    const buttons = root.querySelectorAll('[data-wishlist-button], [data-premium-product-wishlist]');

    buttons.forEach((button) => {
      const product = normalizeProduct(button);
      const active = wishlist.some((item) => isSameProduct(item, product));
      button.classList.toggle('is-active', active);
      button.setAttribute('aria-label', active ? 'Remove from wishlist' : 'Add to wishlist');
      button.setAttribute('aria-pressed', active ? 'true' : 'false');
      button.title = active ? 'Remove from wishlist' : 'Add to wishlist';
    });
  };

  const showToast = (message) => {
    if (!message) return;
    let toast = document.querySelector('[data-gksen-wishlist-toast]');
    if (!toast) {
      toast = document.createElement('div');
      toast.className = 'gksen-wishlist-toast';
      toast.setAttribute('data-gksen-wishlist-toast', '');
      document.body.appendChild(toast);
    }
    toast.textContent = message;
    toast.classList.add('is-visible');
    clearTimeout(showToast.timer);
    showToast.timer = setTimeout(() => toast.classList.remove('is-visible'), 1800);
  };

  const toggleProduct = (product) => {
    if (!product.handle && !product.id) return;
    let wishlist = getWishlist();
    const exists = wishlist.some((item) => isSameProduct(item, product));

    if (exists) {
      wishlist = wishlist.filter((item) => !isSameProduct(item, product));
      setWishlist(wishlist, { action: 'removed' });
      showToast('Removed from wishlist');
    } else {
      wishlist.push(product);
      setWishlist(wishlist, { action: 'added' });
      showToast('Added to wishlist');
    }
  };

  const formatMoney = (cents) => {
    if (typeof cents !== 'number') return '';
    const currency = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'INR';
    try {
      return new Intl.NumberFormat(undefined, { style: 'currency', currency, maximumFractionDigits: 0 }).format(cents / 100);
    } catch (error) {
      return `₹${Math.round(cents / 100).toLocaleString('en-IN')}`;
    }
  };

  const fetchProductDetails = async (item) => {
    if (!item.handle) return item;

    try {
      const response = await fetch(`/products/${encodeURIComponent(item.handle)}.js`, { headers: { Accept: 'application/json' } });
      if (!response.ok) return item;
      const product = await response.json();
      const firstVariant = product.variants && product.variants.length ? product.variants[0] : null;
      const featuredImage = product.featured_image || (product.images && product.images.length ? product.images[0] : '');

      return {
        ...item,
        id: String(product.id || item.id || item.handle),
        handle: product.handle || item.handle,
        title: product.title || item.title,
        url: product.url || `/products/${product.handle || item.handle}`,
        image: featuredImage || item.image,
        price: item.price || formatMoney(product.price),
        variantId: item.variantId || (firstVariant ? firstVariant.id : '')
      };
    } catch (error) {
      return item;
    }
  };

  const renderCard = (item) => `
    <article class="premium-wishlist-card" data-wishlist-card="${escapeAttr(item.handle || item.id)}">
      <a href="${escapeAttr(item.url || '#')}" class="premium-wishlist-card__media" aria-label="${escapeAttr(item.title || 'Product')}">
        ${item.image ? `<img src="${escapeAttr(item.image)}" alt="${escapeAttr(item.title || 'Product')}" loading="lazy" class="premium-wishlist-card__image">` : `<div class="premium-wishlist-card__placeholder"></div>`}
      </a>
      <div class="premium-wishlist-card__content">
        <h3 class="premium-wishlist-card__title"><a href="${escapeAttr(item.url || '#')}">${escapeHtml(item.title || 'Product')}</a></h3>
        ${item.price ? `<p class="premium-wishlist-card__price">${escapeHtml(item.price)}</p>` : ''}
        <div class="premium-wishlist-card__actions">
          ${item.variantId ? `
            <form method="post" action="/cart/add" class="premium-wishlist-card__form">
              <input type="hidden" name="id" value="${escapeAttr(item.variantId)}">
              <input type="hidden" name="quantity" value="1">
              <button type="submit" class="premium-wishlist-card__view">Add to cart</button>
            </form>
          ` : `<a href="${escapeAttr(item.url || '#')}" class="premium-wishlist-card__view">View product</a>`}
          <button type="button" class="premium-wishlist-card__remove" data-wishlist-remove="${escapeAttr(item.handle || item.id)}" aria-label="Remove ${escapeAttr(item.title || 'product')}">Remove</button>
        </div>
      </div>
    </article>
  `;

  const renderWishlistSections = async (root = document) => {
    const sections = root.querySelectorAll('[data-premium-wishlist-section]');
    if (!sections.length) return;

    const wishlist = getWishlist();
    const detailedWishlist = await Promise.all(wishlist.map(fetchProductDetails));

    sections.forEach((section) => {
      const grid = section.querySelector('[data-premium-wishlist-grid]');
      const empty = section.querySelector('[data-premium-wishlist-empty]');
      const count = section.querySelector('[data-premium-wishlist-count]');
      if (!grid) return;

      if (count) count.textContent = detailedWishlist.length;
      if (empty) empty.hidden = detailedWishlist.length > 0;
      grid.hidden = detailedWishlist.length === 0;
      grid.innerHTML = detailedWishlist.map(renderCard).join('');
    });
  };

  document.addEventListener('click', (event) => {
    const productButton = event.target.closest('[data-premium-product-wishlist]');
    if (productButton) {
      event.preventDefault();
      event.stopPropagation();
      toggleProduct(normalizeProduct(productButton));
      return;
    }

    const removeButton = event.target.closest('[data-wishlist-remove]');
    if (removeButton) {
      event.preventDefault();
      const key = removeButton.dataset.wishlistRemove;
      const next = getWishlist().filter((item) => (item.handle || item.id) !== key);
      setWishlist(next, { action: 'removed' });
      showToast('Removed from wishlist');
    }
  });

  document.addEventListener('DOMContentLoaded', () => {
    updateButtons(document);
    updateCounters();
    renderWishlistSections(document);
  });

  document.addEventListener('shopify:section:load', (event) => {
    updateButtons(event.target);
    updateCounters();
    renderWishlistSections(event.target);
  });

  document.addEventListener('premium:wishlist:update', () => {
    updateButtons(document);
    updateCounters();
    renderWishlistSections(document);
  });

  window.PremiumWishlist = {
    get: getWishlist,
    set: setWishlist,
    render: () => renderWishlistSections(document),
    updateButtons: () => updateButtons(document),
    updateCounters,
    toggle: toggleProduct
  };
})();
